import { useAuth0 } from '@auth0/auth0-react';

import { UserType } from '../types';

export enum LoginRequiredErrors {
  INVALID_GRANT = 'invalid_grant',
  LOGIN_REQUIRED = 'login_required',
  MISSING_REFRESH_TOKEN = 'missing_refresh_token',
}

export type GetAccessTokenSilently = ReturnType<typeof useAuth0>['getAccessTokenSilently'];
export type AuthError = { error: string; error_description: string };

export type JwtPayload = {
  sub: string;
  iat: number;
  exp: number;
  iss: string;
  grants: Record<string, unknown>;
};

export type Auth0Config = {
  domain: string;
  clientId: string;
  /**
   * The correct audience for the app to use. This is determined based on the
   * user type.
   *
   * NOTE: Client users will use the monolith audience which is accepted by the
   * Telehealth backend for client-authorized requests and the monolith
   * backend. Provider users will use the Telehealth audience which is accepted
   * by the Telehealth backend and the monolith backend for provider-authorized
   * requests. Ideally, the monolith and Telehealth backends would expect the
   * same API audience. However, presently, they do not. Due to expectations
   * from the monolith about what audience to expect for client requests, we opt
   * to use the monolith audience for client requests to the telehealth backend
   * instead. The audience is set on app initialization and is used for all
   * requests made by the app. Swapping audience after the Auth0 provider has
   * been initialized is not recommended as it can lead to unexpected auth
   * issues in Safari and incognito browsers.
   */
  audience: string;
  scope: string;
  loginUri: string;
  redirectUri: string;
  authorizeTimeoutInSeconds?: number;
  httpTimeoutInSeconds?: number;
};

/**
 * ⚠️ Do **not** change the values here without also changing the values in the
 * universal login page.
 *
 * Purposefully different from our `UserType` enum to decouple uses.
 */
export enum LoginExperience {
  CLIENT = 'client-user',
  PROVIDER = 'provider-user',
}

/**
 * The Classic login page reads `target_login_experience`; ACUL screens only
 * receive authorization params prefixed `ext-`. Both are sent until Classic is
 * retired.
 */
export function loginExperienceAuthorizationParams(loginExperience: LoginExperience) {
  return {
    target_login_experience: loginExperience,
    'ext-target_login_experience': loginExperience,
  };
}

export function loginExperienceToUserType(loginExperience: LoginExperience): UserType {
  switch (loginExperience) {
    case LoginExperience.CLIENT:
      return UserType.CLIENT;
    case LoginExperience.PROVIDER:
      return UserType.PROVIDER;
    default: {
      const _exhaustiveCheck: never = loginExperience;
      throw new Error(`Unsupported login experience: ${_exhaustiveCheck}`);
    }
  }
}

/**
 * Automatically set on the redirect URL by Auth0's universal login page after
 * logging in.
 */
export type CodeExchangeQueryParams = {
  code: string;
  state: string;
};
